import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, Plus, Upload, Trash2, Edit2, RefreshCw, BookOpen,
  Database, Sparkles, CheckCircle2, XCircle, FileText, Wand2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPYQManagement() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [examDetails, setExamDetails] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [pyqs, setPyqs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Add form state
  const [form, setForm] = useState({
    question: "",
    options: { a: "", b: "", c: "", d: "" },
    correct_answer: "a",
    explanation: "",
    difficulty: "medium",
    year: "",
  });
  const [adding, setAdding] = useState(false);

  // Bulk upload state
  const [bulkJson, setBulkJson] = useState("");
  const [bulking, setBulking] = useState(false);

  // Import Wizard state
  const [wizardMode, setWizardMode] = useState("text"); // "text" | "pdf"
  const [wizardText, setWizardText] = useState("");
  const [wizardFile, setWizardFile] = useState(null);
  const [wizardMaxQs, setWizardMaxQs] = useState(50);
  const [wizardParsing, setWizardParsing] = useState(false);
  const [wizardConfirming, setWizardConfirming] = useState(false);
  const [wizardParsedQuestions, setWizardParsedQuestions] = useState(null);

  useEffect(() => {
    fetchExamsAndStats();
  }, []);

  useEffect(() => {
    if (selectedExam) fetchExamDetails();
    else setExamDetails(null);
  }, [selectedExam]);

  useEffect(() => {
    if (selectedExam) fetchPyqs();
  }, [selectedExam, selectedTopic]);

  const fetchExamsAndStats = async () => {
    try {
      const [catalog, statsRes] = await Promise.all([
        axios.get(`${API}/exam-prep/catalog`),
        axios.get(`${API}/exam-prep/admin/pyq/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setExams(catalog.data.exams || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load exams/stats");
      if (err.response?.status === 403) navigate("/dashboard");
    }
  };

  const fetchExamDetails = async () => {
    try {
      const res = await axios.get(`${API}/exam-prep/exam/${selectedExam}`);
      setExamDetails(res.data);
      setSelectedTopic(""); // reset
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPyqs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ exam_id: selectedExam, limit: 100 });
      if (selectedTopic) params.append("topic", selectedTopic);
      const res = await axios.get(
        `${API}/exam-prep/admin/pyq?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPyqs(res.data.questions || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load PYQs");
    } finally {
      setLoading(false);
    }
  };

  const addPyq = async () => {
    if (!selectedExam || !selectedTopic) {
      toast.error("Please select an exam and topic first");
      return;
    }
    if (!form.question.trim() || !form.options.a || !form.options.b || !form.options.c || !form.options.d) {
      toast.error("Please fill in the question and all 4 options");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        exam_id: selectedExam,
        topic: selectedTopic,
        questions: [
          {
            question: form.question.trim(),
            options: form.options,
            correct_answer: form.correct_answer,
            explanation: form.explanation.trim(),
            difficulty: form.difficulty,
            year: form.year ? parseInt(form.year, 10) : null,
          },
        ],
      };
      const res = await axios.post(
        `${API}/exam-prep/admin/seed-pyq`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.inserted > 0) {
        toast.success("PYQ added successfully!");
        setForm({
          question: "",
          options: { a: "", b: "", c: "", d: "" },
          correct_answer: "a",
          explanation: "",
          difficulty: "medium",
          year: "",
        });
        fetchPyqs();
        fetchExamsAndStats();
      } else {
        toast.error(res.data.errors?.[0] || "Failed to add PYQ");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to add PYQ");
    } finally {
      setAdding(false);
    }
  };

  const bulkUpload = async () => {
    if (!selectedExam || !selectedTopic) {
      toast.error("Please select an exam and topic first");
      return;
    }
    let questions;
    try {
      questions = JSON.parse(bulkJson);
      if (!Array.isArray(questions)) throw new Error("Must be a JSON array");
    } catch (err) {
      toast.error("Invalid JSON. Expected an array of question objects.");
      return;
    }
    setBulking(true);
    try {
      const res = await axios.post(
        `${API}/exam-prep/admin/seed-pyq`,
        { exam_id: selectedExam, topic: selectedTopic, questions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Bulk upload: ${res.data.inserted} inserted, ${res.data.skipped} skipped`
      );
      if (res.data.errors?.length > 0) {
        console.warn("PYQ upload errors:", res.data.errors);
        toast.warning(`${res.data.errors.length} errors — check console`);
      }
      setBulkJson("");
      fetchPyqs();
      fetchExamsAndStats();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulking(false);
    }
  };

  const deletePyq = async (id) => {
    if (!window.confirm("Delete this PYQ? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/exam-prep/admin/pyq/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("PYQ deleted");
      fetchPyqs();
      fetchExamsAndStats();
    } catch (err) {
      toast.error("Failed to delete PYQ");
    }
  };

  // ==================== Import Wizard ====================
  const runWizardParse = async () => {
    if (!selectedExam || !selectedTopic) {
      toast.error("Please select an exam and topic first");
      return;
    }
    setWizardParsing(true);
    setWizardParsedQuestions(null);
    try {
      let res;
      if (wizardMode === "text") {
        if (!wizardText.trim()) {
          toast.error("Please paste some PYQ text first");
          setWizardParsing(false);
          return;
        }
        res = await axios.post(
          `${API}/exam-prep/admin/pyq/wizard/parse-text`,
          {
            exam_id: selectedExam,
            topic: selectedTopic,
            raw_text: wizardText,
            max_questions: wizardMaxQs,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // PDF mode
        if (!wizardFile) {
          toast.error("Please select a PDF file first");
          setWizardParsing(false);
          return;
        }
        const fd = new FormData();
        fd.append("exam_id", selectedExam);
        fd.append("topic", selectedTopic);
        fd.append("max_questions", wizardMaxQs);
        fd.append("file", wizardFile);
        res = await axios.post(
          `${API}/exam-prep/admin/pyq/wizard/parse-pdf`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }
      const parsed = res.data.questions || [];
      setWizardParsedQuestions(parsed);
      if (parsed.length === 0) {
        toast.error("AI could not extract any valid questions. Try a clearer format.");
      } else {
        toast.success(
          `AI parsed ${parsed.length} question${parsed.length > 1 ? "s" : ""}! Review and confirm below.`
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to parse. Try again.");
    } finally {
      setWizardParsing(false);
    }
  };

  const editParsedQuestion = (idx, field, value, optionKey = null) => {
    const updated = [...wizardParsedQuestions];
    if (optionKey) {
      updated[idx] = {
        ...updated[idx],
        options: { ...updated[idx].options, [optionKey]: value },
      };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setWizardParsedQuestions(updated);
  };

  const removeParsedQuestion = (idx) => {
    setWizardParsedQuestions(wizardParsedQuestions.filter((_, i) => i !== idx));
  };

  const confirmWizardImport = async () => {
    if (!wizardParsedQuestions || wizardParsedQuestions.length === 0) return;
    setWizardConfirming(true);
    try {
      const res = await axios.post(
        `${API}/exam-prep/admin/pyq/wizard/confirm`,
        {
          exam_id: selectedExam,
          topic: selectedTopic,
          questions: wizardParsedQuestions,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Wizard import: ${res.data.inserted} inserted, ${res.data.skipped} skipped`
      );
      setWizardParsedQuestions(null);
      setWizardText("");
      setWizardFile(null);
      fetchPyqs();
      fetchExamsAndStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setWizardConfirming(false);
    }
  };

  const jsonExample = JSON.stringify(
    [
      {
        question: "The capital of India is ___",
        options: {
          a: "Mumbai",
          b: "New Delhi",
          c: "Kolkata",
          d: "Chennai",
        },
        correct_answer: "b",
        explanation: "New Delhi has been the capital of India since 1911.",
        difficulty: "easy",
        year: 2023,
      },
    ],
    null,
    2
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="mb-4"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
        </Button>

        <div className="mb-6" data-testid="pyq-admin-header">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            PYQ Management
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Seed and manage Previous Year Questions to boost AI accuracy from 85% → 95%
          </p>
        </div>

        {/* Stats overview */}
        {stats && (
          <Card className="mb-6 border-0 shadow-md" data-testid="pyq-stats-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Database className="w-5 h-5 text-blue-600" />
                PYQ Library Stats
              </CardTitle>
              <CardDescription>
                Total across all exams:{" "}
                <span className="font-bold text-blue-700">{stats.grand_total}</span> PYQs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats.exams.length === 0 ? (
                <p className="text-sm text-gray-600">No PYQs seeded yet. Start by selecting an exam below.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stats.exams.map((e) => (
                    <div
                      key={e.exam_id}
                      className="bg-gradient-to-br from-white to-blue-50 p-3 rounded-lg border border-blue-100"
                    >
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {e.exam_name}
                      </p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">
                        {e.total_pyqs}
                      </p>
                      <p className="text-xs text-gray-500">
                        Across {Object.keys(e.topic_breakdown).length} topics
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Exam & Topic Selectors */}
        <Card className="mb-6 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              Select Exam & Topic
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="exam-select">Exam</Label>
                <select
                  id="exam-select"
                  value={selectedExam}
                  onChange={(e) => setSelectedExam(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md h-10"
                  data-testid="exam-select"
                >
                  <option value="">— Choose an exam —</option>
                  {exams.map((e) => (
                    <option key={e.exam_id} value={e.exam_id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="topic-select">Topic</Label>
                <select
                  id="topic-select"
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  disabled={!examDetails}
                  className="w-full p-2 border border-gray-300 rounded-md h-10 disabled:bg-gray-100"
                  data-testid="topic-select"
                >
                  <option value="">— All topics (view) / choose to add —</option>
                  {examDetails?.sections.flatMap((s) =>
                    s.topics.map((t) => (
                      <option key={`${s.name}::${t}`} value={t}>
                        [{s.short_name}] {t}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add / Bulk / Wizard / List Tabs */}
        {selectedExam && (
          <Tabs defaultValue="list" className="w-full">
            <TabsList className="grid grid-cols-4 w-full mb-4">
              <TabsTrigger value="list" data-testid="tab-list-pyqs">
                <FileText className="w-4 h-4 mr-1 hidden sm:inline" /> Existing ({pyqs.length})
              </TabsTrigger>
              <TabsTrigger value="add" data-testid="tab-add-pyq">
                <Plus className="w-4 h-4 mr-1 hidden sm:inline" /> Add One
              </TabsTrigger>
              <TabsTrigger value="bulk" data-testid="tab-bulk-upload">
                <Upload className="w-4 h-4 mr-1 hidden sm:inline" /> Bulk JSON
              </TabsTrigger>
              <TabsTrigger value="wizard" data-testid="tab-wizard">
                <Wand2 className="w-4 h-4 mr-1 hidden sm:inline" /> AI Wizard
              </TabsTrigger>
            </TabsList>

            {/* List Existing PYQs */}
            <TabsContent value="list">
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      {selectedTopic || "All topics"} ({pyqs.length} PYQs)
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchPyqs}>
                      <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                    </div>
                  ) : pyqs.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      No PYQs found. Add some via the &apos;Add One&apos; or &apos;Bulk Upload&apos; tabs.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                      {pyqs.map((q) => (
                        <PyqItem key={q.id} pyq={q} onDelete={() => deletePyq(q.id)} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Add Single PYQ */}
            <TabsContent value="add">
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-base">Add New PYQ</CardTitle>
                  <CardDescription>
                    {selectedTopic ? (
                      <>
                        Adding to: <Badge variant="outline">{selectedTopic}</Badge>
                      </>
                    ) : (
                      "Please select a specific topic above to add a PYQ."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="pyq-question">Question *</Label>
                    <Textarea
                      id="pyq-question"
                      rows={3}
                      value={form.question}
                      onChange={(e) => setForm({ ...form, question: e.target.value })}
                      placeholder="Type the exam question here..."
                      data-testid="pyq-question-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {["a", "b", "c", "d"].map((opt) => (
                      <div key={opt}>
                        <Label htmlFor={`opt-${opt}`}>Option {opt.toUpperCase()} *</Label>
                        <Input
                          id={`opt-${opt}`}
                          value={form.options[opt]}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              options: { ...form.options, [opt]: e.target.value },
                            })
                          }
                          data-testid={`pyq-option-${opt}-input`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="correct">Correct Answer *</Label>
                      <select
                        id="correct"
                        value={form.correct_answer}
                        onChange={(e) => setForm({ ...form, correct_answer: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md h-10"
                        data-testid="pyq-correct-select"
                      >
                        <option value="a">A</option>
                        <option value="b">B</option>
                        <option value="c">C</option>
                        <option value="d">D</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="diff">Difficulty</Label>
                      <select
                        id="diff"
                        value={form.difficulty}
                        onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md h-10"
                        data-testid="pyq-difficulty-select"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="year">Year (optional)</Label>
                      <Input
                        id="year"
                        type="number"
                        min="1990"
                        max={new Date().getFullYear()}
                        value={form.year}
                        onChange={(e) => setForm({ ...form, year: e.target.value })}
                        placeholder="e.g. 2023"
                        data-testid="pyq-year-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="explanation">Explanation (recommended)</Label>
                    <Textarea
                      id="explanation"
                      rows={3}
                      value={form.explanation}
                      onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                      placeholder="Step-by-step solution or reasoning..."
                      data-testid="pyq-explanation-input"
                    />
                  </div>

                  <Button
                    onClick={addPyq}
                    disabled={adding || !selectedTopic}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                    data-testid="add-pyq-btn"
                  >
                    {adding ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    {adding ? "Adding..." : "Add PYQ"}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bulk Upload */}
            <TabsContent value="bulk">
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-base">Bulk Upload (JSON)</CardTitle>
                  <CardDescription>
                    Paste a JSON array of PYQ objects. Perfect for uploading 10+ questions at once.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded-md text-xs text-blue-900">
                    <p className="font-semibold mb-1">Expected format (JSON array):</p>
                    <pre className="whitespace-pre-wrap font-mono text-[10px] leading-tight bg-white p-2 rounded border border-blue-200 overflow-x-auto">
                      {jsonExample}
                    </pre>
                  </div>
                  <Textarea
                    rows={12}
                    value={bulkJson}
                    onChange={(e) => setBulkJson(e.target.value)}
                    placeholder='[{"question": "...", "options": {...}, "correct_answer": "b", ...}]'
                    className="font-mono text-xs"
                    data-testid="bulk-json-input"
                  />
                  <Button
                    onClick={bulkUpload}
                    disabled={bulking || !bulkJson.trim() || !selectedTopic}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
                    data-testid="bulk-upload-btn"
                  >
                    {bulking ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {bulking ? "Uploading..." : "Upload Bulk PYQs"}
                  </Button>
                  {!selectedTopic && (
                    <p className="text-xs text-amber-600 text-center">
                      ⚠️ Please select a specific topic above to enable bulk upload.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Import Wizard */}
            <TabsContent value="wizard">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Wand2 className="w-5 h-5 text-violet-600" />
                    AI Import Wizard
                  </CardTitle>
                  <CardDescription>
                    Paste raw PYQ text or upload a PDF — AI extracts questions, options, and correct answers into structured format. Review, edit, and confirm before importing.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!selectedTopic && (
                    <div className="p-3 bg-amber-50 rounded-md text-xs text-amber-800 border border-amber-200">
                      ⚠️ Please select a specific topic above to use the wizard.
                    </div>
                  )}

                  {/* Mode Toggle */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant={wizardMode === "text" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWizardMode("text")}
                      data-testid="wizard-mode-text"
                    >
                      <FileText className="w-3 h-3 mr-1" /> Paste Text
                    </Button>
                    <Button
                      variant={wizardMode === "pdf" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWizardMode("pdf")}
                      data-testid="wizard-mode-pdf"
                    >
                      <Upload className="w-3 h-3 mr-1" /> Upload PDF
                    </Button>
                    <div className="flex items-center gap-2 ml-auto text-xs">
                      <Label htmlFor="max-qs">Max questions:</Label>
                      <Input
                        id="max-qs"
                        type="number"
                        min="1"
                        max="100"
                        value={wizardMaxQs}
                        onChange={(e) => setWizardMaxQs(parseInt(e.target.value) || 50)}
                        className="w-20 h-8"
                        data-testid="wizard-max-qs"
                      />
                    </div>
                  </div>

                  {/* Input Area */}
                  {wizardMode === "text" ? (
                    <Textarea
                      rows={12}
                      value={wizardText}
                      onChange={(e) => setWizardText(e.target.value)}
                      placeholder={`Paste PYQ text here. Example format:\n\nQ1. What is 40% of 250?\nA) 80\nB) 100\nC) 120\nD) 140\nAnswer: B\nExplanation: 40% of 250 = 100\n\nQ2. ...`}
                      className="font-mono text-xs"
                      data-testid="wizard-text-input"
                    />
                  ) : (
                    <div className="p-4 border-2 border-dashed border-violet-300 rounded-md text-center bg-violet-50">
                      <Upload className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                      <input
                        type="file"
                        accept=".pdf,.txt"
                        onChange={(e) => setWizardFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="wizard-pdf-input"
                        data-testid="wizard-pdf-input"
                      />
                      <label
                        htmlFor="wizard-pdf-input"
                        className="cursor-pointer text-sm text-violet-700 hover:underline"
                      >
                        {wizardFile ? (
                          <span>
                            📄 {wizardFile.name} ({(wizardFile.size / 1024).toFixed(1)} KB)
                          </span>
                        ) : (
                          "Click to select a PDF or TXT file (max 5MB)"
                        )}
                      </label>
                    </div>
                  )}

                  <Button
                    onClick={runWizardParse}
                    disabled={wizardParsing || !selectedTopic}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600"
                    data-testid="wizard-parse-btn"
                  >
                    {wizardParsing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        AI parsing... (this may take 20-60 seconds)
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Parse with AI
                      </>
                    )}
                  </Button>

                  {/* Preview + Edit + Confirm */}
                  {wizardParsedQuestions && (
                    <div className="pt-2 border-t space-y-3" data-testid="wizard-preview">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="font-semibold text-sm">
                          Preview: {wizardParsedQuestions.length} question
                          {wizardParsedQuestions.length !== 1 ? "s" : ""} extracted
                        </h3>
                        <Button
                          onClick={confirmWizardImport}
                          disabled={wizardConfirming || wizardParsedQuestions.length === 0}
                          size="sm"
                          className="bg-gradient-to-r from-green-600 to-emerald-600"
                          data-testid="wizard-confirm-btn"
                        >
                          {wizardConfirming ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                          )}
                          Confirm & Import All
                        </Button>
                      </div>
                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                        {wizardParsedQuestions.map((q, idx) => (
                          <WizardParsedItem
                            key={idx}
                            index={idx}
                            question={q}
                            onEdit={editParsedQuestion}
                            onRemove={() => removeParsedQuestion(idx)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
      </div>
    </div>
  );
}

const WizardParsedItem = ({ index, question, onEdit, onRemove }) => {
  const [editing, setEditing] = useState(false);

  return (
    <div
      className="p-3 bg-violet-50 rounded-lg border border-violet-200"
      data-testid={`wizard-parsed-${index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <Badge className="bg-violet-600 text-white text-xs">Q{index + 1}</Badge>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(!editing)}
            className="h-7 text-xs"
            data-testid={`wizard-edit-${index}`}
          >
            <Edit2 className="w-3 h-3 mr-1" /> {editing ? "Done" : "Edit"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-7 text-xs text-red-600"
            data-testid={`wizard-remove-${index}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-2 text-xs">
          <Textarea
            rows={2}
            value={question.question}
            onChange={(e) => onEdit(index, "question", e.target.value)}
            className="text-xs"
            data-testid={`wizard-q-input-${index}`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {["a", "b", "c", "d"].map((opt) => (
              <div key={opt} className="flex items-center gap-1">
                <span className="font-bold uppercase text-xs">{opt}.</span>
                <Input
                  value={question.options?.[opt] || ""}
                  onChange={(e) => onEdit(index, "options", e.target.value, opt)}
                  className="text-xs h-7"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Label>Correct:</Label>
            <select
              value={question.correct_answer}
              onChange={(e) => onEdit(index, "correct_answer", e.target.value)}
              className="p-1 border border-gray-300 rounded text-xs"
              data-testid={`wizard-correct-${index}`}
            >
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
              <option value="d">D</option>
            </select>
            <Label>Difficulty:</Label>
            <select
              value={question.difficulty || "medium"}
              onChange={(e) => onEdit(index, "difficulty", e.target.value)}
              className="p-1 border border-gray-300 rounded text-xs"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <Textarea
            rows={2}
            placeholder="Explanation"
            value={question.explanation || ""}
            onChange={(e) => onEdit(index, "explanation", e.target.value)}
            className="text-xs"
          />
        </div>
      ) : (
        <div className="text-xs">
          <p className="text-gray-900 font-medium mb-1">{question.question}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5 mt-1">
            {["a", "b", "c", "d"].map((opt) => (
              <div
                key={opt}
                className={`px-2 py-0.5 rounded text-xs ${
                  question.correct_answer === opt
                    ? "bg-green-100 border border-green-300 font-semibold text-green-900"
                    : "text-gray-700"
                }`}
              >
                <span className="font-bold uppercase">{opt}.</span> {question.options?.[opt]}
                {question.correct_answer === opt && " ✓"}
              </div>
            ))}
          </div>
          {question.explanation && (
            <p className="mt-1.5 text-xs text-gray-600 italic">
              💡 {question.explanation}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <Badge className="text-[10px] bg-gray-100 text-gray-700">
              {question.difficulty || "medium"}
            </Badge>
            {question.year && (
              <Badge className="text-[10px] bg-blue-100 text-blue-700">
                {question.year}
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PyqItem = ({ pyq, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition"
      data-testid={`pyq-item-${pyq.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className="bg-blue-100 text-blue-800 text-xs">{pyq.topic}</Badge>
            <Badge
              className={`text-xs ${
                pyq.difficulty === "easy"
                  ? "bg-green-100 text-green-800"
                  : pyq.difficulty === "hard"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {pyq.difficulty}
            </Badge>
            {pyq.year && (
              <Badge variant="outline" className="text-xs">
                {pyq.year}
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              Correct: <span className="uppercase font-bold">{pyq.correct_answer}</span>
            </span>
          </div>
          <p className="text-sm text-gray-800 line-clamp-2">{pyq.question}</p>
          {expanded && (
            <div className="mt-2 space-y-1 text-xs">
              {["a", "b", "c", "d"].map((k) => (
                <div
                  key={k}
                  className={`p-1.5 rounded ${
                    pyq.correct_answer === k
                      ? "bg-green-50 border border-green-200"
                      : "bg-gray-50"
                  }`}
                >
                  <span className="font-bold uppercase mr-1.5">{k}.</span>
                  {pyq.options?.[k]}
                  {pyq.correct_answer === k && (
                    <CheckCircle2 className="w-3 h-3 inline ml-1 text-green-600" />
                  )}
                </div>
              ))}
              {pyq.explanation && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-blue-900">
                  <p className="font-semibold text-xs">Explanation:</p>
                  <p className="text-xs">{pyq.explanation}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-xs h-7"
            data-testid={`pyq-toggle-${pyq.id}`}
          >
            {expanded ? "Hide" : "View"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-xs h-7 text-red-600 hover:text-red-800"
            data-testid={`pyq-delete-${pyq.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};
